import { config } from "@/config/environment";
import { pwaUpdateManager } from "@/utilities/pwa-update-manager";

import { registerSW } from "virtual:pwa-register";

type Snapshot = { needRefresh: boolean; offlineReady: boolean };

const listeners = new Set<() => void>();

let snapshot: Snapshot = { needRefresh: false, offlineReady: false };

let updateServiceWorkerImpl: ((reloadPage?: boolean) => Promise<void>) | null =
  null;

let initialized = false;

function emit() {
  for (const l of listeners) {
    l();
  }
}

function setSnapshot(partial: Partial<Snapshot>) {
  snapshot = { ...snapshot, ...partial };
  emit();
}

export function subscribeServiceWorkerState(onStoreChange: () => void) {
  listeners.add(onStoreChange);
  return () => {
    listeners.delete(onStoreChange);
  };
}

export function getServiceWorkerStateSnapshot(): Snapshot {
  return snapshot;
}

export function getServerServiceWorkerStateSnapshot(): Snapshot {
  return { needRefresh: false, offlineReady: false };
}

export function dismissServiceWorkerNeedRefresh() {
  setSnapshot({ needRefresh: false });
}

export function dismissServiceWorkerOfflineReady() {
  setSnapshot({ offlineReady: false });
}

export function getUpdateServiceWorker(): ((
  reloadPage?: boolean
) => Promise<void>) | null {
  return updateServiceWorkerImpl;
}

const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;

let updateChecksStarted = false;

/** Long-lived tabs only see a new `sw.js` if we poll; browsers otherwise wait up to 24h. */
function startServiceWorkerUpdateChecks(
  registration: ServiceWorkerRegistration
): void {
  if (updateChecksStarted) {
    return;
  }
  updateChecksStarted = true;

  const checkForUpdate = () => {
    void registration.update();
  };

  window.setInterval(checkForUpdate, UPDATE_CHECK_INTERVAL_MS);

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      checkForUpdate();
    }
  });
}

/**
 * Register the app service worker as early as possible (call from `main.tsx`).
 * Keeps a single registration; React hooks read state via `subscribeServiceWorkerState`.
 * With `registerType: "autoUpdate"`, a new SW that activates reloads the page.
 */
export function initServiceWorkerRegistration(): void {
  if (initialized) {
    return;
  }
  initialized = true;

  updateServiceWorkerImpl = registerSW({
    immediate: true,
    onRegisteredSW(swUrl: string, registration: ServiceWorkerRegistration | undefined) {
      if (config.debug.enabled) {
        console.log("Service Worker registered:", swUrl, registration);
      }
      pwaUpdateManager.startInitialRegistrationTimer();
      if (registration) {
        startServiceWorkerUpdateChecks(registration);
      }
    },
    onRegisterError(error: unknown) {
      if (config.debug.enabled) {
        console.error("Service Worker registration error:", error);
      }
    },
    onNeedRefresh() {
      if (pwaUpdateManager.onUpdateDetected()) {
        setSnapshot({ needRefresh: true });
      }
    },
    onOfflineReady() {
      setSnapshot({ offlineReady: true });
    }
  });
}
